/** Cleo item hover — what the product is for in this project (not the visible Cleo Description). */
export type CleoItemHoverExplanation = {
  whatThisIs: string;
  whyYouNeedIt: string;
};

export type ProposalItemHoverSource = "cleo" | "derived" | "generic";

export const GENERIC_PROPOSAL_HOVER_FALLBACK: CleoItemHoverExplanation = {
  whatThisIs: "This is one of the components included in your proposal.",
  whyYouNeedIt: "It forms part of the system design prepared for your project.",
};

export type CleoItemDescriptions = {
  cleoDescription: string;
  customerExplanation: CleoItemHoverExplanation;
};

export function wordCount(text: string): number {
  return String(text ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

export function serializeCustomerExplanation(payload: CleoItemHoverExplanation): string {
  return JSON.stringify({
    whatThisIs: String(payload.whatThisIs ?? "").trim(),
    whyYouNeedIt: String(payload.whyYouNeedIt ?? "").trim(),
  });
}

export function parseCustomerExplanationStored(raw: string | null | undefined): CleoItemHoverExplanation | null {
  const text = String(raw ?? "").trim();
  if (!text) return null;
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    const whatThisIs = String(parsed.whatThisIs ?? "").trim();
    const whyYouNeedIt = String(parsed.whyYouNeedIt ?? "").trim();
    if (whatThisIs && whyYouNeedIt) {
      return { whatThisIs, whyYouNeedIt };
    }
  } catch {
    /* legacy plain-text fallback */
  }
  return null;
}

/** Resolve hover copy for customer-facing proposal lines — never returns null. */
export function resolveProposalItemHoverExplanation(input: {
  customerExplanation?: string | null;
  visibleDescription?: string | null;
  productTitle?: string | null;
}): { explanation: CleoItemHoverExplanation; source: ProposalItemHoverSource } {
  const parsed = parseCustomerExplanationStored(input.customerExplanation);
  if (parsed) {
    return { explanation: parsed, source: "cleo" };
  }

  const title = String(input.productTitle ?? "").trim();
  const visible = String(input.visibleDescription ?? "").trim();

  if (visible || title) {
    const whatThisIs = visible
      ? visible.length > 240
        ? `${visible.slice(0, 237).trim()}…`
        : visible
      : title
        ? `${title} is included in your proposal.`
        : GENERIC_PROPOSAL_HOVER_FALLBACK.whatThisIs;

    const whyYouNeedIt = title
      ? `It supports the ${title} scope in the integrated system design prepared for your project.`
      : "It forms part of the system design prepared for your project.";

    return { explanation: { whatThisIs, whyYouNeedIt }, source: "derived" };
  }

  return { explanation: GENERIC_PROPOSAL_HOVER_FALLBACK, source: "generic" };
}

export function hasStoredCleoDescriptions(item: {
  cleoDescription?: string | null;
  customerExplanation?: string | null;
}): boolean {
  const desc = String(item.cleoDescription ?? "").trim();
  const hover = parseCustomerExplanationStored(item.customerExplanation);
  return Boolean(desc && hover);
}

const HYPE_PATTERNS =
  /stunning|breathtaking|incredible|unparalleled|unmatched|revolutionary|next-level|world-class|cutting-edge/gi;

const BANNED_PHRASES =
  /\bdepends on system configuration\b|\bdesigned for integration\b|\bversatile solution\b|\bhigh-performance system\b/gi;

/** Neutral replacements for common LLM hype words — applied before rejection. */
const HYPE_WORD_REPLACEMENTS: Array<[RegExp, string]> = [
  [/\bstunning\b/gi, "sharp"],
  [/\bbreathtaking\b/gi, "immersive"],
  [/\bincredible\b/gi, "strong"],
  [/\bunparalleled\b/gi, "excellent"],
  [/\bunmatched\b/gi, "excellent"],
  [/\brevolutionary\b/gi, "advanced"],
  [/\bnext-level\b/gi, "high-quality"],
  [/\bworld-class\b/gi, "high-quality"],
  [/\bcutting-edge\b/gi, "modern"],
];

export function sanitizeCleoMarketingHype(text: string): string {
  let out = String(text ?? "").trim();
  for (const [pattern, replacement] of HYPE_WORD_REPLACEMENTS) {
    out = out.replace(pattern, replacement);
  }
  return out.replace(/\s{2,}/g, " ").trim();
}

export function explainCleoDescriptionRejection(text: string): string | null {
  const t = String(text ?? "").trim();
  const wc = wordCount(t);
  if (wc < 15) return `cleoDescription too short (${wc} words, min 15)`;
  if (wc > 80) return `cleoDescription too long (${wc} words, max 80)`;
  BANNED_PHRASES.lastIndex = 0;
  if (BANNED_PHRASES.test(t)) return "cleoDescription contains banned phrase";
  HYPE_PATTERNS.lastIndex = 0;
  const hype = t.match(HYPE_PATTERNS);
  if (hype?.length) return `cleoDescription contains hype word: ${hype[0]}`;
  return null;
}

export function explainCleoHoverRejection(
  hover: CleoItemHoverExplanation,
  cleoDescription: string,
): string | null {
  const what = String(hover.whatThisIs ?? "").trim();
  const why = String(hover.whyYouNeedIt ?? "").trim();
  if (!what || !why) return "hover missing whatThisIs or whyYouNeedIt";
  const total = wordCount(`${what} ${why}`);
  if (total < 35) return `hover too short (${total} words, min 35)`;
  if (total > 150) return `hover too long (${total} words, max 150)`;
  BANNED_PHRASES.lastIndex = 0;
  if (BANNED_PHRASES.test(what) || BANNED_PHRASES.test(why)) return "hover contains banned phrase";
  HYPE_PATTERNS.lastIndex = 0;
  const hype = `${what} ${why}`.match(HYPE_PATTERNS);
  if (hype?.length) return `hover contains hype word: ${hype[0]}`;
  const cleoNorm = cleoDescription.trim().toLowerCase();
  if (cleoNorm && what.toLowerCase() === cleoNorm) return "hover whatThisIs duplicates cleoDescription";
  if (cleoNorm.length > 40 && what.toLowerCase().includes(cleoNorm.slice(0, 40))) {
    return "hover whatThisIs largely duplicates cleoDescription";
  }
  return null;
}

export function isCleoDescriptionRejected(text: string): boolean {
  const t = String(text ?? "").trim();
  const wc = wordCount(t);
  if (wc < 15 || wc > 80) return true;
  BANNED_PHRASES.lastIndex = 0;
  if (BANNED_PHRASES.test(t)) return true;
  HYPE_PATTERNS.lastIndex = 0;
  if (HYPE_PATTERNS.test(t)) return true;
  return false;
}

export function isCleoHoverExplanationRejected(
  hover: CleoItemHoverExplanation,
  cleoDescription: string,
): boolean {
  const what = String(hover.whatThisIs ?? "").trim();
  const why = String(hover.whyYouNeedIt ?? "").trim();
  if (!what || !why) return true;
  const total = wordCount(`${what} ${why}`);
  if (total < 35 || total > 150) return true;
  BANNED_PHRASES.lastIndex = 0;
  if (BANNED_PHRASES.test(what) || BANNED_PHRASES.test(why)) return true;
  HYPE_PATTERNS.lastIndex = 0;
  if (HYPE_PATTERNS.test(what) || HYPE_PATTERNS.test(why)) return true;
  const cleoNorm = cleoDescription.trim().toLowerCase();
  if (cleoNorm && what.toLowerCase() === cleoNorm) return true;
  if (cleoNorm.length > 40 && what.toLowerCase().includes(cleoNorm.slice(0, 40))) return true;
  return false;
}

export function parseCleoDescriptionsFromLlmJson(raw: string): CleoItemDescriptions | null {
  const text = String(raw ?? "").trim();
  if (!text) return null;
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    let cleoDescription = sanitizeCleoMarketingHype(String(parsed.cleoDescription ?? "").trim());
    const customer = parsed.customerExplanation;
    if (!cleoDescription || !customer || typeof customer !== "object" || customer === null) return null;
    const ce = customer as Record<string, unknown>;
    const hover: CleoItemHoverExplanation = {
      whatThisIs: sanitizeCleoMarketingHype(String(ce.whatThisIs ?? "").trim()),
      whyYouNeedIt: sanitizeCleoMarketingHype(String(ce.whyYouNeedIt ?? "").trim()),
    };
    if (isCleoDescriptionRejected(cleoDescription)) return null;
    if (isCleoHoverExplanationRejected(hover, cleoDescription)) return null;
    return { cleoDescription, customerExplanation: hover };
  } catch {
    return null;
  }
}
